import type {
    OrderGroupId} from "@@/shared/order-groups/order-group-id";
import {
    orderGroupIdZodSchema,
} from "@@/shared/order-groups/order-group-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    CurrencyCode} from "../../currencies/currencies";
import {
    currencyCodeZodSchema,
} from "../../currencies/currencies";
import type {
    IsoAndUnixTimestamp} from "../../date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "../../date-time/iso-and-unix";
import type {
    ManagePaymentCommandId} from "../../manage-payments/manage-payment-command-id";
import {
    managePaymentCommandIdZodSchema,
} from "../../manage-payments/manage-payment-command-id";
import type { UserId} from "../../users/user-id";
import { userIdZodSchema } from "../../users/user-id";
import type { PaymentId} from "../payment-id";
import { paymentIdFactory, paymentIdZodSchema } from "../payment-id";
import type {
    PaymentProvider} from "../payment-providers";
import {
    paymentProviderZodSchema,
} from "../payment-providers";

type OnPremisePaymentCancelled = {
    _id: PaymentId;
    _type: "ONPREMISE_PAYMENT";

    amount: number;
    _createdAt: IsoAndUnixTimestamp;
    currencyCode: CurrencyCode;
    orderGroupId: OrderGroupId;
    paymentProvider: PaymentProvider;
    userId: UserId;
    status: "CANCELLED";
    /**
     * Contains id's/tags for grouping the event with others.
     * Like a payment change command id that can be used to find and
     * group all events related to a single payment change command
     */
    cancellationReferences: {
        managePaymentCommandId?: ManagePaymentCommandId;
    };
    cancelledBy: UserId;
};

const onPremisePaymentCancelledZodScheme: z.ZodSchema<OnPremisePaymentCancelled> =
    z.object({
        _id: paymentIdZodSchema,
        _type: z.literal("ONPREMISE_PAYMENT"),
        _createdAt: isoAndUnixZodSchema,
        amount: z.number().min(0),
        currencyCode: currencyCodeZodSchema,
        orderGroupId: orderGroupIdZodSchema,
        paymentProvider: paymentProviderZodSchema,
        receiptEmail: z.string().optional(),
        status: z.literal("CANCELLED"),
        userId: userIdZodSchema,
        /**
         * Contains id's/tags for grouping the event with others.
         * Like a payment change command id that can be used to find and
         * group all events related to a single payment change command
         */
        cancellationReferences: z.object({
            managePaymentCommandId: managePaymentCommandIdZodSchema.optional(),
        }),
        cancelledBy: userIdZodSchema,
    });

type OnPremisePaymentPending = {
    _id: PaymentId;
    _type: "ONPREMISE_PAYMENT";

    amount: number;
    _createdAt: IsoAndUnixTimestamp;
    currencyCode: CurrencyCode;
    orderGroupId: OrderGroupId;
    paymentProvider: PaymentProvider;
    receiptEmail?: string;
    status: "PENDING";
    userId: UserId;
};

const onPremisePaymentPendingZodScheme: z.ZodSchema<OnPremisePaymentPending> =
    z.object({
        _id: paymentIdZodSchema,
        _type: z.literal("ONPREMISE_PAYMENT"),
        _createdAt: isoAndUnixZodSchema,
        amount: z.number().min(0),
        currencyCode: currencyCodeZodSchema,
        orderGroupId: orderGroupIdZodSchema,
        paymentProvider: paymentProviderZodSchema,
        receiptEmail: z.string().optional(),
        status: z.literal("PENDING"),
        userId: userIdZodSchema,
    });

//type OnPremisePaymentFulfilled<ProviderData extends unknown = unknown> = {
type OnPremisePaymentFulfilled = {
    _id: PaymentId;
    _type: "ONPREMISE_PAYMENT";
    _createdAt: IsoAndUnixTimestamp;
    amount: number;
    currencyCode: CurrencyCode;
    orderGroupId: OrderGroupId;
    paymentProvider: PaymentProvider;
    status: "FULFILLED"; // ?: Denna saknades av någon anledning 8 feb 2022, la till /emanuel
    userId: UserId;
};

const onPremisePaymentFulfilledZodScheme: z.ZodSchema<OnPremisePaymentFulfilled> =
    z.object({
        _id: paymentIdZodSchema,
        _type: z.literal("ONPREMISE_PAYMENT"),
        _createdAt: isoAndUnixZodSchema,
        amount: z.number().min(0),
        currencyCode: currencyCodeZodSchema,
        orderGroupId: orderGroupIdZodSchema,
        paymentProvider: paymentProviderZodSchema,
        receiptEmail: z.string().optional(),
        status: z.literal("FULFILLED"),
        userId: userIdZodSchema,
    });

type OnPremisePayment =
    | OnPremisePaymentPending
    | OnPremisePaymentCancelled
    | OnPremisePaymentFulfilled;

// | OnPremisePaymentFulfilled<ProviderData>;

const onPremisePaymentCancelledFactory = (
    params: Except<
        OnPremisePaymentCancelled,
        "_id" | "_type" | "status" | "_createdAt"
    >,
) => {
    const payment: OnPremisePaymentCancelled = {
        ...params,
        _id: paymentIdFactory(),
        _type: "ONPREMISE_PAYMENT",
        status: "CANCELLED",
        _createdAt: nowIsoAndUnix(),
    };

    return payment;
};
const onPremisePaymentFulfilledFactory = (
    params: Except<
        OnPremisePaymentFulfilled,
        "_id" | "_type" | "status" | "_createdAt"
    >,
) => {
    const payment: OnPremisePaymentFulfilled = {
        ...params,
        _id: paymentIdFactory(),
        _type: "ONPREMISE_PAYMENT",
        status: "FULFILLED",
        _createdAt: nowIsoAndUnix(),
    };

    return payment;
};

const onPremisePaymentPendingFactory = (
    params: Except<
        OnPremisePaymentPending,
        "_id" | "_type" | "status" | "_createdAt"
    >,
) => {
    const payment: OnPremisePaymentPending = {
        ...params,
        _id: paymentIdFactory(),
        _type: "ONPREMISE_PAYMENT",
        status: "PENDING",
        _createdAt: nowIsoAndUnix(),
    };

    return payment;
};

const onPremiseZodSchema = z.union([
    onPremisePaymentPendingZodScheme,
    onPremisePaymentCancelledZodScheme,
    onPremisePaymentFulfilledZodScheme,
]);

export {
    onPremisePaymentCancelledFactory,
    onPremisePaymentCancelledZodScheme,
    onPremisePaymentFulfilledFactory,
    onPremisePaymentFulfilledZodScheme,
    onPremisePaymentPendingFactory,
    onPremisePaymentPendingZodScheme,
    onPremiseZodSchema,
};
export type {
    OnPremisePayment,
    OnPremisePaymentCancelled,
    OnPremisePaymentFulfilled,
    OnPremisePaymentPending,
};
